import React, { useState, useEffect, useRef } from 'react';
import { RunBtn, Tabs, Panel } from '../../components/ui';
import HelpPanel from './HelpPanel.jsx';

const DIRS = [[-1,0],[1,0],[0,-1],[0,1]]; // up,down,left,right

function idx(x,y,w){return x+w*y}

export default function QLearningLab() {
  const [activeTab, setActiveTab] = useState('simulate');
  const W = 5, H = 5;
  const start = [0,0];
  const goal = [4,4];
  const walls = useRef(new Set([idx(1,2,W), idx(2,2,W)]));

  const [agent, setAgent] = useState(start);
  const [q, setQ] = useState(() => new Float32Array(W*H*4).fill(0));
  const [alpha, setAlpha] = useState(0.3);
  const [gamma, setGamma] = useState(0.95);
  const [epsilon, setEpsilon] = useState(0.2);
  const [running, setRunning] = useState(false);
  const [episode, setEpisode] = useState(0);
  const [maxSteps, setMaxSteps] = useState(200);
  const [speed, setSpeed] = useState(80);
  const [log, setLog] = useState([]);

  useEffect(() => { setAgent(start); }, []);

  const resetQ = () => { setQ(new Float32Array(W*H*4).fill(0)); setEpisode(0); setLog([]); setAgent(start); };

  function legal(x,y){ if (x<0||y<0||x>=W||y>=H) return false; if (walls.current.has(idx(x,y,W))) return false; return true }

  function stepState(pos, a){ const nx = pos[0]+DIRS[a][0], ny = pos[1]+DIRS[a][1]; if (!legal(nx,ny)) return pos; return [nx,ny]; }

  function isGoal(pos){ return pos[0]===goal[0] && pos[1]===goal[1]; }

  function chooseAction(stateIdx, eps){ if (Math.random() < eps) return Math.floor(Math.random()*4); const base = stateIdx*4; let best=0, bi=0; for(let a=0;a<4;a++){ const v = q[base+a]; if (a===0||v>best){ best=v; bi=a }} return bi }

  async function runEpisode(learn=true){
    let pos = [...start]; let total=0;
    for(let t=0;t<maxSteps;t++){
      const s = idx(pos[0],pos[1],W);
      const a = chooseAction(s, epsilon);
      const npos = stepState(pos,a);
      const reward = isGoal(npos) ? 1 : -0.01;
      total += reward;
      if (learn){
        const sidx = s*4 + a;
        const ns = idx(npos[0],npos[1],W);
        let maxNext = -1e9; for(let aa=0;aa<4;aa++){ maxNext = Math.max(maxNext, q[ns*4 + aa]) }
        const old = q[sidx];
        const target = reward + gamma * (isGoal(npos) ? 0 : maxNext);
        const nv = old + alpha*(target - old);
        // update immutable-style
        setQ(prev => { const next = new Float32Array(prev); next[sidx]=nv; return next });
      }
      pos = npos;
      setAgent([...pos]);
      if (isGoal(pos)) break;
      await new Promise(r => setTimeout(r, speed));
      if (!running) break;
    }
    setEpisode(e => e+1);
    setLog(l => [total].concat(l).slice(0,200));
    return total;
  }

  useEffect(() => {
    let cancel = false;
    (async ()=>{
      while(running && !cancel){ await runEpisode(true); await new Promise(r=>setTimeout(r,50)); }
    })();
    return ()=>{ cancel=true };
  }, [running, alpha, gamma, epsilon, maxSteps, speed]);

  // ---- Experiments: fast synchronous simulator (no UI delays) ----
  function simulateEpisodesLocal({alpha: a, gamma: g, eps: e, episodes=50}){
    const N = W*H; const Qlocal = new Float32Array(N*4).fill(0);
    function chooseLocal(s, epsLocal){ if (Math.random() < epsLocal) return Math.floor(Math.random()*4); let bi=0; for(let aa=1;aa<4;aa++) if (Qlocal[s*4+aa] > Qlocal[s*4+bi]) bi=aa; return bi }
    function stepLocal(pos, act){ const nx = pos[0]+DIRS[act][0], ny = pos[1]+DIRS[act][1]; if (nx<0||ny<0||nx>=W||ny>=H) return pos; if (walls.current.has(idx(nx,ny,W))) return pos; return [nx,ny]; }
    function isGoalLocal(p){ return p[0]===goal[0] && p[1]===goal[1]; }
    let returns = [];
    for(let ep=0; ep<episodes; ep++){
      let pos = [...start]; let total=0;
      for(let t=0;t<maxSteps;t++){
        const s = idx(pos[0],pos[1],W);
        const aact = chooseLocal(s, e);
        const npos = stepLocal(pos, aact);
        const reward = isGoalLocal(npos) ? 1 : -0.01;
        total += reward;
        const sidx = s*4 + aact; const ns = idx(npos[0],npos[1],W);
        let maxNext = -1e9; for(let aa=0;aa<4;aa++) maxNext = Math.max(maxNext, Qlocal[ns*4 + aa]);
        const old = Qlocal[sidx]; const target = reward + g * (isGoalLocal(npos) ? 0 : maxNext);
        Qlocal[sidx] = old + a*(target - old);
        pos = npos;
        if (isGoalLocal(pos)) break;
      }
      returns.push(total);
    }
    return returns.reduce((s,x)=>s+x,0)/returns.length;
  }

  const [expResults, setExpResults] = useState([]);
  const [expAlphaStart, setExpAlphaStart] = useState(0.05);
  const [expAlphaEnd, setExpAlphaEnd] = useState(0.6);
  const [expEpsStart, setExpEpsStart] = useState(0.01);
  const [expEpsEnd, setExpEpsEnd] = useState(0.5);
  const [expSteps, setExpSteps] = useState(4);
  const [expEpisodes, setExpEpisodes] = useState(60);

  async function runSweep(){
    const alphas = []; for(let i=0;i<expSteps;i++) alphas.push(expAlphaStart + (expAlphaEnd-expAlphaStart)*(i/(Math.max(1,expSteps-1))));
    const epss = []; for(let j=0;j<expSteps;j++) epss.push(expEpsStart + (expEpsEnd-expEpsStart)*(j/(Math.max(1,expSteps-1))));
    const results = [];
    for(const aVal of alphas){
      for(const eVal of epss){
        const avg = simulateEpisodesLocal({alpha: aVal, gamma, eps: eVal, episodes: expEpisodes});
        results.push({alpha: aVal.toFixed(3), eps: eVal.toFixed(3), avg});
        // yield to event loop
        await new Promise(r=>setTimeout(r,20));
      }
    }
    setExpResults(results);
  }

  function renderCell(x,y){
    const index = idx(x,y,W);
    const isWall = walls.current.has(index);
    const isStart = x===start[0] && y===start[1];
    const isG = x===goal[0] && y===goal[1];
    const base = index*4;
    let bestA = 0, bestV = q[base];
    for(let a=1;a<4;a++){ if (q[base+a] > bestV){ bestV = q[base+a]; bestA = a }}
    const policyChar = ['↑','↓','←','→'][bestA];
    return (
      <div key={index} style={{ border: '1px solid var(--b0)', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: isWall ? 'var(--b0)' : isG ? 'var(--gcyan)' : 'transparent' }}>
        {isStart && <div style={{ position: 'absolute', top: 4, left: 6, fontSize: 11, color: 'var(--t3)' }}>S</div>}
        {isG && <div style={{ position: 'absolute', top: 4, left: 6, fontSize: 11, color: 'var(--t3)' }}>G</div>}
        {agent[0]===x && agent[1]===y ? <div style={{ width: 28, height: 28, borderRadius: 4, background: 'var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--void)', fontWeight: 800 }}>{'⦿'}</div> : <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{policyChar}</div>
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>{bestV.toFixed(2)}</div>
        </div>}
      </div>
    );
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 18 }}>
        <div style={{ position: 'absolute', right: 24, top: 88 }}>
          <Tabs tabs={[{id:'simulate', label:'Simulate'}, {id:'experiments', label:'Experiments'}, {id:'theory', label:'Theory'}]} active={activeTab} onChange={setActiveTab} />
        </div>
        <div style={{ minWidth: 380 }}>
          <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 800 }}>Q-Learning Lab</div>
          <div style={{ marginTop: 6, color: 'var(--t2)' }}>Tabular Q-Learning on a small gridworld — train in-browser and inspect learned policy.</div>

          {activeTab === 'simulate' && (
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(5,64px)', gap: 6 }}>
              {Array.from({length:H}).map((_,y)=> Array.from({length:W}).map((__,x)=> renderCell(x,y))).flat()}
            </div>
          )}

          {activeTab === 'experiments' && (
            <Panel title="Parameter Sweep" noPad>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'var(--t3)' }}>Alpha range</div>
                    <div style={{ display:'flex', gap:8 }}><input type="number" value={expAlphaStart} onChange={e=>setExpAlphaStart(parseFloat(e.target.value)||0)} style={{width:86}} /> <input type="number" value={expAlphaEnd} onChange={e=>setExpAlphaEnd(parseFloat(e.target.value)||0)} style={{width:86}} /></div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'var(--t3)' }}>Epsilon range</div>
                    <div style={{ display:'flex', gap:8 }}><input type="number" value={expEpsStart} onChange={e=>setExpEpsStart(parseFloat(e.target.value)||0)} style={{width:86}} /> <input type="number" value={expEpsEnd} onChange={e=>setExpEpsEnd(parseFloat(e.target.value)||0)} style={{width:86}} /></div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <div style={{ fontSize:12 }}>Grid steps <input type="number" min={1} max={8} value={expSteps} onChange={e=>setExpSteps(parseInt(e.target.value)||1)} style={{width:86, marginLeft:6}} /></div>
                  <div style={{ fontSize:12 }}>Episodes per combo <input type="number" min={1} max={500} value={expEpisodes} onChange={e=>setExpEpisodes(parseInt(e.target.value)||10)} style={{width:86, marginLeft:6}} /></div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={runSweep} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RUN SWEEP</button>
                </div>
                <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--b0)', padding: 8 }}>
                  {expResults.length === 0 ? <div style={{ color: 'var(--t3)' }}>No results yet.</div> : expResults.map((r,i)=>(<div key={i} style={{ fontSize: 13, padding: 6, borderBottom: '1px dashed var(--b0)' }}>α={r.alpha} · ε={r.eps} → avg return {r.avg.toFixed(3)}</div>))}
                </div>
              </div>
            </Panel>
          )}
        </div>

        <div style={{ flex: 1, maxWidth: 520 }}>
          <div style={{ marginBottom: 12 }}>
            <HelpPanel title="Q-Learning — Quick Tutorial" items={[
              ['Goal', 'Tabular environment: reach goal cell for +1 reward.'],
              ['Epsilon', 'Probability of random action — higher yields exploration.'],
              ['Alpha', 'Learning rate used to update the Q-table.'],
              ['Step Episode', 'Run a single episode; Run toggles continuous training.'],
            ]} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <RunBtn running={running} onRun={() => setRunning(true)} onStop={() => setRunning(false)} label="RUN" />
            <button onClick={() => runEpisode(true)} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>STEP EPISODE</button>
            <button onClick={() => { setAgent(start); }} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET AGENT</button>
            <button onClick={resetQ} style={{ padding: '8px 12px', border: '1px solid var(--b1)', background: 'transparent', color: 'var(--t2)', borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>RESET Q</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label style={{ fontSize: 12 }}>Alpha
              <input type="range" min="0.01" max="1" step="0.01" value={alpha} onChange={e=>setAlpha(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Gamma
              <input type="range" min="0" max="1" step="0.01" value={gamma} onChange={e=>setGamma(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Epsilon
              <input type="range" min="0" max="1" step="0.01" value={epsilon} onChange={e=>setEpsilon(parseFloat(e.target.value))} />
            </label>
            <label style={{ fontSize: 12 }}>Speed ms
              <input type="range" min="0" max="300" step="10" value={speed} onChange={e=>setSpeed(parseInt(e.target.value))} />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>Episode: <strong style={{ color: 'var(--t1)' }}>{episode}</strong></div>
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Recent Episode Rewards</div>
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--b0)', padding: 8, borderRadius: 4 }}>
                {log.length === 0 ? <div style={{ color: 'var(--t3)' }}>No episodes yet.</div> : log.map((v,i)=>(<div key={i} style={{ fontSize: 13 }}>{(log.length-i)+'. '} {v.toFixed(3)}</div>))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
